/**
 * Time.
 *
 * Timestamps cross the wire as ISO 8601 UTC strings and are typed as
 * `IsoDateTime`. They are converted to a Date exactly once, here, at the
 * boundary — never with a bare `new Date(apiValue)` inside a component.
 *
 * Everything a staff member sees is in the client's timezone (Asia/Kathmandu,
 * UTC+05:45 — a 45 minute offset, which is precisely why no arithmetic in this
 * file uses a hardcoded hour count).
 */

declare const isoBrand: unique symbol;

export type IsoDateTime = string & { readonly [isoBrand]: 'IsoDateTime' };

/** Brand a value coming from the API. Throws on anything unparseable. */
export function isoDateTime(raw: string): IsoDateTime {
  if (Number.isNaN(Date.parse(raw))) {
    throw new Error(`Not an ISO 8601 timestamp: ${raw}`);
  }
  return raw as IsoDateTime;
}

export function toDate(value: IsoDateTime): Date {
  return new Date(value);
}

export function fromDate(value: Date): IsoDateTime {
  return value.toISOString() as IsoDateTime;
}

export interface TimeFormat {
  /** IANA zone, from client config. */
  readonly timezone: string;
}

const dateFormatters = new Map<string, Intl.DateTimeFormat>();

function formatter(key: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const existing = dateFormatters.get(key);
  if (existing) return existing;
  const created = new Intl.DateTimeFormat('en-GB', options);
  dateFormatters.set(key, created);
  return created;
}

/** "24 Aug" — a table column, where the year is usually noise. */
export function formatDay(value: IsoDateTime, format: TimeFormat): string {
  return formatter(`day:${format.timezone}`, {
    day: '2-digit',
    month: 'short',
    timeZone: format.timezone,
  }).format(toDate(value));
}

/** "24 Aug 2026" */
export function formatDate(value: IsoDateTime, format: TimeFormat): string {
  return formatter(`date:${format.timezone}`, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: format.timezone,
  }).format(toDate(value));
}

/** "09:30" — 24 hour, because a rental day starts before dawn and ends late. */
export function formatTime(value: IsoDateTime, format: TimeFormat): string {
  return formatter(`time:${format.timezone}`, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: format.timezone,
  }).format(toDate(value));
}

/**
 * "24 Aug 2026, 09:30" — day, month, year, then the time.
 *
 * This used to leave the year off, which was fine while every booking was for
 * next week. It stopped being fine once the calendar could look a year ahead:
 * a guest booking from abroad in October for next March, and a row reading
 * "12 Mar" that could be either. The client asked for day, month, year
 * outright.
 */
export function formatDateTime(value: IsoDateTime, format: TimeFormat): string {
  return `${formatDate(value, format)}, ${formatTime(value, format)}`;
}

/**
 * Value for a `datetime-local` input, in the client's timezone.
 *
 * Every entry screen accepts a back-dated timestamp: staff enter Tuesday's
 * booking on Thursday. This is how a form is seeded with a *suggested* time
 * that the user can freely change — it is never forced to now().
 */
export function toLocalInputValue(value: IsoDateTime, format: TimeFormat): string {
  const parts = formatter(`parts:${format.timezone}`, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: format.timezone,
  }).formatToParts(toDate(value));
  const find = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '00';
  return `${find('year')}-${find('month')}-${find('day')}T${find('hour')}:${find('minute')}`;
}

/** Whole days between two instants, rounded up — a rental day is a started day. */
export function rentalDays(start: IsoDateTime, end: IsoDateTime): number {
  const ms = toDate(end).getTime() - toDate(start).getTime();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86_400_000);
}

export function isBefore(a: IsoDateTime, b: IsoDateTime): boolean {
  return toDate(a).getTime() < toDate(b).getTime();
}
